<script setup>
import { ClipboardDocumentIcon } from "@heroicons/vue/24/solid";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  display: {
    type: String,
    required: false,
  },
  value: {
    type: String,
    required: true,
  },
  copyTitle: {
    type: String,
    required: false,
  },
  showIcon: {
    type: String,
    required: false,
    default: "none",
    validator: (value) => ["none", "left", "right"].includes(value),
  },
  showHoverText: {
    type: Boolean,
    required: false,
    default: true,
  },
  showOnClickText: {
    type: Boolean,
    required: false,
    default: true,
  },
  ariaLabel: {
    type: String,
    required: false,
  },
});

const showCopied = ref(false);

function handleCopy(e) {
  e.stopPropagation();
  navigator.clipboard.writeText(props.value);
  showCopied.value = true;
  setTimeout(() => {
    showCopied.value = false;
  }, 750);
}
</script>

<template>
  <button
    type="button"
    class="group m-0 inline-flex cursor-copy select-none items-center border-0 bg-transparent p-0"
    @click="handleCopy"
    :aria-label="ariaLabel || t('msg.copyToClipboard', { value })"
  >
    <ClipboardDocumentIcon
      v-if="showIcon == 'left'"
      ref="reference"
      class="mr-1 mt-0.5 inline h-4 w-4 cursor-pointer text-[#ccd1c4] hover:text-white"
    />

    <span v-if="display">
      {{ display }}
    </span>
    <span v-else>
      {{ value }}
    </span>

    <ClipboardDocumentIcon
      v-if="showIcon == 'right'"
      ref="reference"
      class="mr-1 mt-0.5 inline h-4 w-4 cursor-pointer text-[#ccd1c4] hover:text-white"
    />

    <span v-if="showHoverText || showOnClickText" aria-hidden="true" class="relative ml-1 text-primary">
      <span
        v-if="showHoverText"
        class="opacity-0 transition-opacity duration-300 ease-in-out"
        :class="{ 'group-hover:opacity-100': !showCopied }"
      >
        {{ t("msg.copy") }}
      </span>

      <span
        v-if="showOnClickText && showCopied"
        class="absolute left-0 animate-fly-up"
        role="status"
        aria-live="polite"
      >
        {{ copyTitle || t("msg.copied!") }}
      </span>
    </span>
  </button>
</template>
